import { PlusIcon } from '@heroicons/react/24/solid';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import Button from '@/components/UI/Button';
import LoadingTable from '@/components/UI/loading/LoadingTable';
import Modal from '@/components/UI/Modal';
import { tasksTableHeader } from '@/constants/Rover/Applications';
import { useApplicationTeamMembers } from '@/hooks/ReactQueryHooks/useApplicationQuery';
import { useSherpaVendors } from '@/hooks/ReactQueryHooks/useVendorQuery';
import { isObjectEmpty } from '@/utils';
import type { GetTasksResData } from '../../../../../../api/Sherpa/Tdm/types';
//Assets imports
import { useRoverTasks } from '../../../../../../hooks/ReactQueryHooks/useTasksQuery';
import type { TaskTableHeaderInterface } from '../../../../../../types/Tdm';
import NewTaskForm from '../../NewTaskForm';
import DueDiligenceTableFooter from '../DueDiligenceTableFooter';
import DueDiligenceTaskRow from './DueDiligenceTaskRow';

export default function DueDiligenceTasksTable({ phase }: { phase: { label: string; value: string } }) {
  const { applicationId } = useParams();
  const [fetchParams, setFetchParams] = useState({
    page: 1,
    perPage: 10,
    application_id: applicationId,
    phase: phase.value,
    orderBy: 'sortIndex',
    order: 'asc',
  });

  const [showModal, setShowModal] = useState<{ [key: string]: boolean }>({
    task: false,
    newTask: false,
  });

  useEffect(() => {
    if (phase) {
      setFetchParams((prevState) => {
        return { ...prevState, phase: phase.value };
      });
    }
  }, [phase]);

  const getApplicationTeamMembers = useApplicationTeamMembers({
    applicationId: applicationId!,
  });

  const applicationTeamMembers = getApplicationTeamMembers?.data?.data;

  const teams = [];

  if (applicationTeamMembers?.applicationLead) {
    teams.push(applicationTeamMembers.applicationLead);
  }

  if (applicationTeamMembers?.members) {
    teams.push(...applicationTeamMembers.members.map((member: any) => member?.user));
  }

  const fetchTasks = useRoverTasks({
    params: { ...fetchParams },
  });
  const tasks = fetchTasks.data?.data;

  const tasksTableRef = useRef<HTMLDivElement | null>(null);

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page > 1 ? prevState.page - 1 : 1,
      }));
    } else if (direction === 'next') {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page === totalPage ? totalPage : prevState.page + 1,
      }));
    }
  };

  const perPageHandler = (e: ChangeEvent<HTMLSelectElement>) => {
    setFetchParams((prevState) => ({
      ...prevState,
      page: 1,
      perPage: Number(e.target.value),
    }));
  };

  const modalHandler = (modalName: string) => {
    setShowModal((prevState) => {
      return {
        ...prevState,
        [modalName]: !prevState[modalName],
      };
    });
  };

  const createNewTdmHandler = () => {
    modalHandler('newTask');
  };

  const tableHeader = [...tasksTableHeader];

  const totalPage = Math.ceil(fetchTasks.data?.meta.total / fetchParams.perPage);

  const fetchVendors = useSherpaVendors({});
  const vendors = fetchVendors?.data?.data;

  return (
    <div ref={tasksTableRef} id='tasks-table' className='rounded-md bg-white w-full'>
      {showModal.newTask && (
        <Modal customStyle='max-w-full'>
          <NewTaskForm phase={phase} teams={teams} formModalHandler={modalHandler} />
        </Modal>
      )}
      <div className='border-b py-3 flex justify-between items-center'>
        <h3 className='font-bold text-bold border-l-4 p-4 border-green-500 capitalize'>Tasks</h3>
        {applicationTeamMembers && !isObjectEmpty(applicationTeamMembers?.applicationLead) && (
          <div className='px-3'>
            <Button label='Add' btnStyle='btn-primary' btnSize='md' icon={<PlusIcon />} onClick={createNewTdmHandler} />
          </div>
        )}
      </div>
      {fetchTasks.isLoading && <LoadingTable />}
      {/* table header */}
      <div className='w-full overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='flex justify-between px-5 py-4 bg-gray-200'>
              {tableHeader.map((head: TaskTableHeaderInterface) => {
                return (
                  <th
                    key={head.name}
                    className={twMerge(['flex gap-2 text-neutralColor-normal text-left text-xs uppercase', head.customClassName])}
                  >
                    {head.name}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {fetchTasks.isSuccess &&
              tasks.map((task: GetTasksResData) => <DueDiligenceTaskRow key={task.id} task={task} vendors={vendors} />)}
          </tbody>
        </table>
      </div>
      <DueDiligenceTableFooter
        pageSize={totalPage > 0 ? totalPage : 1}
        page={fetchParams.page}
        perPage={fetchParams.perPage}
        perPageHandler={perPageHandler}
        pageChangeHandler={onPageChangeHandler}
      />
    </div>
  );
}
